import React, { useState } from 'react';

function isMobile() {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 600;
}

// Hard equipment limits per slot
const EQUIPMENT_LIMITS = {
    'HANDS': 2,
    'LEGS': 1,
    'FEET': 1,
    'HEAD': 1,
    'CHEST': 1,
    'ANY': 3
};

function GearPanel({ gear, teamPlayers, teamGear, onEquip, onUnequip, isEquipping = false, canModifyGear = true }) {
    const [showEquipModal, setShowEquipModal] = useState(false);

    // Debug logging
    console.log(`GearPanel "${gear.name}" - canModifyGear:`, canModifyGear);

    const handleGearClick = () => {
        console.log(`GearPanel "${gear.name}" clicked - canModifyGear:`, canModifyGear);
        if (canModifyGear) {
            console.log(`GearPanel "${gear.name}" - Opening modal`);
            setShowEquipModal(true);
        } else {
            console.log(`GearPanel "${gear.name}" - Modal blocked by canModifyGear`);
        }
    };

    const handleEquipToPlayer = (characterSheetId) => {
        if (onEquip) {
            onEquip(gear.id, characterSheetId);
        }
        setShowEquipModal(false);
    };

    const handleUnequip = () => {
        if (onUnequip) {
            onUnequip(gear.id);
        }
        setShowEquipModal(false);
    };

    const assignedPlayer = gear.character_sheet
        ? teamPlayers.find(p => p.character_sheet_id === gear.character_sheet)
        : null;

    // Calculate equipment counts per player per slot
    const getPlayerEquipmentCounts = (characterSheetId) => {
        const playerGear = teamGear.filter(g => g.character_sheet === characterSheetId);
        const counts = {};

        // Initialize all slots to 0
        Object.keys(EQUIPMENT_LIMITS).forEach(slot => {
            counts[slot] = 0;
        });

        // Count equipment per slot
        playerGear.forEach(g => {
            if (counts[g.slot] !== undefined) {
                counts[g.slot]++;
            }
        });

        return counts;
    };

    // Check if a player can equip more gear in a specific slot
    const canPlayerEquipSlot = (characterSheetId, slot) => {
        const counts = getPlayerEquipmentCounts(characterSheetId);
        const current = counts[slot] || 0;
        const limit = EQUIPMENT_LIMITS[slot] || 1;
        const canEquip = current < limit;

        return canEquip;
    };

    const getSlotColor = (slot) => {
        const colors = {
            'ANY': '#718096',
            'HANDS': '#4a90e2',
            'CHEST': '#7ed321',
            'LEGS': '#f5a623',
            'FEET': '#d0021b',
            'HEAD': '#9013fe'
        };
        return colors[slot] || '#666';
    };

    const getEquipmentStatus = () => {
        if (!assignedPlayer) return { text: 'AVAILABLE', color: '#ed8936' };

        // For HANDS slot, show both gear pieces as EQUIPPED if assigned to player
        // For other slots, use the equipped flag
        if (gear.slot === 'HANDS') {
            // All HANDS items assigned to a player should show as EQUIPPED
            return { text: 'EQUIPPED', color: '#48bb78' };
        } else {
            // For single-gear slots, use the equipped flag
            if (gear.equipped) return { text: 'EQUIPPED', color: '#48bb78' };
            return { text: 'HELD', color: '#4299e1' };
        }
    };

    const equipStatus = getEquipmentStatus();

    const bebasFont = {
        fontFamily: 'Bebas Neue, sans-serif',
        letterSpacing: '1px',
    };

    return (
        <>
            <div
                style={{
                    backgroundColor: (gear.slot === 'HANDS' && assignedPlayer) || gear.equipped ? '#2d5a27' : '#23272f',
                    borderRadius: '10px',
                    padding: isMobile() ? '8px' : '14px',
                    marginBottom: isMobile() ? '10px' : '14px',
                    cursor: canModifyGear ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    border: (gear.slot === 'HANDS' && assignedPlayer) || gear.equipped ? '2.5px solid #48bb78' : '2.5px solid #444',
                    boxShadow: (gear.slot === 'HANDS' && assignedPlayer) || gear.equipped ? '0 0 8px 2px #48bb7855' : '0 1px 4px #1118',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: isMobile() ? 220 : 320,
                    maxWidth: isMobile() ? 340 : 420,
                    width: '100%',
                    gap: isMobile() ? 4 : 7,
                    position: 'relative',
                    ...bebasFont
                }}
                onClick={handleGearClick}
                onMouseEnter={(e) => {
                    const isEquippedStyle = (gear.slot === 'HANDS' && assignedPlayer) || gear.equipped;
                    e.target.style.backgroundColor = isEquippedStyle ? '#22543d' : '#2d3748';
                    e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                    const isEquippedStyle = (gear.slot === 'HANDS' && assignedPlayer) || gear.equipped;
                    e.target.style.backgroundColor = isEquippedStyle ? '#2d5a27' : '#4a5568';
                    e.target.style.transform = 'translateY(0px)';
                }}
            >
                {/* Gear Name */}
                <div style={{
                    width: isMobile() ? Math.round(326 * 0.45) : 260,
                    height: isMobile() ? Math.round(192 * 0.45) : 86,
                    backgroundImage: 'url(/art/nameTextBackground.png)',
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    marginBottom: isMobile() ? 4 : 8,
                    position: 'relative',
                    zIndex: 1,
                    border: '2px solid #444',
                    boxShadow: '0 2px 8px #0008',
                }}>
                    <span style={{
                        display: 'block',
                        width: '90%',
                        textAlign: 'center',
                        color: '#fff',
                        textShadow: '2px 2px 8px #000, 0 0 2px #48bb78',
                        textTransform: 'uppercase',
                        ...bebasFont,
                        position: 'relative',
                        zIndex: 2,
                        background: 'none',
                        boxShadow: 'none',
                        filter: 'none',
                        fontSize: isMobile() ? 'clamp(12px, 7vw, 18px)' : 'clamp(18px, 3vw, 28px)',
                        lineHeight: 1.1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        maxWidth: '100%',
                    }}>{gear.name}</span>
                </div>

                {/* Equipment Status Badge */}
                <div style={{
                    fontSize: isMobile() ? 14 : 16,
                    color: equipStatus.color,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    marginBottom: 4,
                    fontWeight: 'bold',
                    ...bebasFont,
                    border: `1px solid ${equipStatus.color}`,
                }}>
                    {equipStatus.text}
                </div>

                {/* Slot Information */}
                <div style={{
                    fontSize: isMobile() ? 18 : 24,
                    color: getSlotColor(gear.slot),
                    marginBottom: 0,
                    fontWeight: 'bold',
                    ...bebasFont,
                    border: '1.5px solid #7ed321',
                    borderRadius: '5px',
                    padding: isMobile() ? '2px 10px' : '4px 18px',
                    background: '#181c22',
                    boxShadow: '0 1px 2px #0006',
                    textAlign: 'center',
                }}>
                    {gear.slot}
                </div>

                {/* Assigned Player */}
                <div style={{
                    fontSize: isMobile() ? 17 : 22,
                    color: assignedPlayer ? '#f6e05e' : '#a0aec0',
                    marginBottom: 0,
                    ...bebasFont,
                    border: assignedPlayer ? '1.5px solid #f6e05e' : '1.5px solid #a0aec0',
                    borderRadius: '5px',
                    padding: isMobile() ? '2px 10px' : '4px 18px',
                    background: assignedPlayer ? '#23272f' : '#181c22',
                    boxShadow: '0 1px 2px #0006',
                    textAlign: 'center',
                }}>
                    {assignedPlayer ? assignedPlayer.name : 'Not Equipped'}
                </div>

                {/* Gear Description */}
                <div style={{
                    fontSize: isMobile() ? 15 : 20,
                    color: '#e2e8f0',
                    lineHeight: '1.3',
                    background: 'linear-gradient(90deg,#23272f 60%,#181c22 100%)',
                    padding: isMobile() ? '6px 12px' : '10px 20px',
                    borderRadius: '5px',
                    textAlign: 'center',
                    ...bebasFont,
                    border: '1.5px solid #4299e1',
                    boxShadow: '0 1px 2px #0006',
                    minHeight: isMobile() ? 28 : 36,
                    maxHeight: isMobile() ? 60 : 80,
                    overflow: 'auto',
                }}>
                    {gear.description || 'No description available'}
                </div>

                {/* Loading indicator */}
                {isEquipping && (
                    <div style={{
                        marginTop: '4px',
                        fontSize: isMobile() ? 14 : 18,
                        color: '#63b3ed',
                        textAlign: 'center',
                        ...bebasFont,
                        border: '1.5px solid #63b3ed',
                        borderRadius: '5px',
                        background: '#181c22',
                        padding: '4px 12px',
                        alignSelf: 'center',
                        boxShadow: '0 1px 2px #0006',
                    }}>
                        <div style={{
                            display: 'inline-block',
                            animation: 'spin 1s linear infinite',
                            marginRight: '8px'
                        }}>⚙️</div>
                        Processing...
                    </div>
                )}
            </div>

            {/* Equipment Modal */}
            {showEquipModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.88)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'linear-gradient(120deg,#23272f 70%,#181c22 100%)',
                        borderRadius: '14px',
                        padding: isMobile() ? '12px' : '18px',
                        maxWidth: isMobile() ? '98vw' : '420px',
                        width: isMobile() ? '98vw' : '420px',
                        color: '#f7fafc',
                        maxHeight: isMobile() ? '90vh' : '80vh',
                        overflowY: 'auto',
                        border: '2.5px solid #48bb78',
                        boxShadow: '0 0 16px 2px #48bb7855',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: isMobile() ? 8 : 12,
                    }}>
                        <h3 style={{
                            margin: '0 0 8px 0',
                            color: '#63b3ed',
                            textAlign: 'center',
                            fontSize: isMobile() ? '20px' : '26px',
                            ...bebasFont,
                            border: '2px solid #63b3ed',
                            borderRadius: '7px',
                            padding: isMobile() ? '2px 8px' : '4px 16px',
                            background: '#181c22',
                        }}>
                            {gear.name}
                        </h3>

                        <div style={{
                            background: 'linear-gradient(90deg,#23272f 60%,#181c22 100%)',
                            padding: isMobile() ? '3px 7px' : '6px 12px',
                            borderRadius: '5px',
                            marginBottom: 0,
                            fontSize: isMobile() ? 13 : 16,
                            lineHeight: '1.3',
                            ...bebasFont,
                            border: '1.5px solid #4299e1',
                            boxShadow: '0 1px 2px #0006',
                        }}>
                            {gear.description}
                        </div>

                        {/* Slot Limit Info */}
                        <div style={{
                            fontSize: '12px',
                            color: '#a0aec0',
                            backgroundColor: 'rgba(160,174,192,0.1)',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            textAlign: 'center',
                            ...bebasFont
                        }}>
                            SLOT LIMIT: {EQUIPMENT_LIMITS[gear.slot] || 1} {gear.slot} GEAR PIECE{EQUIPMENT_LIMITS[gear.slot] > 1 ? 'S' : ''} MAX PER PLAYER
                        </div>

                        {assignedPlayer ? (
                            <div style={{
                                margin: 0,
                                padding: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: isMobile() ? 6 : 10,
                            }}>
                                <div style={{
                                    textAlign: 'center',
                                    color: '#f6e05e',
                                    fontSize: isMobile() ? 15 : 18,
                                    ...bebasFont,
                                    border: '1.5px solid #f6e05e',
                                    borderRadius: '5px',
                                    background: '#23272f',
                                    padding: isMobile() ? '2px 8px' : '4px 16px',
                                    marginBottom: 0,
                                }}>
                                    Currently with: <strong>{assignedPlayer.name}</strong>
                                </div>
                                <div style={{
                                    textAlign: 'center',
                                    color: gear.slot === 'HANDS' ? '#48bb78' : (gear.equipped ? '#48bb78' : '#4299e1'),
                                    fontSize: isMobile() ? 13 : 16,
                                    ...bebasFont,
                                    border: '1.5px solid #48bb78',
                                    borderRadius: '5px',
                                    background: '#181c22',
                                    padding: isMobile() ? '2px 8px' : '4px 16px',
                                }}>
                                    Status: {gear.slot === 'HANDS' ? 'EQUIPPED' : (gear.equipped ? 'EQUIPPED' : 'HELD')}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: 6 }}>
                                    <button
                                        onClick={handleUnequip}
                                        disabled={isEquipping}
                                        style={{
                                            padding: isMobile() ? '6px 12px' : '10px 20px',
                                            backgroundColor: '#e53e3e',
                                            color: 'white',
                                            border: '2px solid #e53e3e',
                                            borderRadius: '6px',
                                            cursor: isEquipping ? 'not-allowed' : 'pointer',
                                            opacity: isEquipping ? 0.6 : 1,
                                            fontSize: isMobile() ? 13 : 15,
                                            ...bebasFont,
                                            boxShadow: '0 1px 2px #0006',
                                        }}
                                    >
                                        {isEquipping ? 'Unequipping...' : 'Unequip'}
                                    </button>
                                    <button
                                        onClick={() => setShowEquipModal(false)}
                                        style={{
                                            padding: isMobile() ? '6px 12px' : '10px 20px',
                                            backgroundColor: '#4a5568',
                                            color: 'white',
                                            border: '2px solid #4a5568',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: isMobile() ? 13 : 15,
                                            ...bebasFont,
                                            boxShadow: '0 1px 2px #0006',
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div style={{
                                margin: 0,
                                padding: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: isMobile() ? 6 : 10,
                            }}>
                                <div style={{
                                    marginBottom: 0,
                                    textAlign: 'center',
                                    color: '#63b3ed',
                                    fontSize: isMobile() ? 15 : 18,
                                    ...bebasFont,
                                    border: '1.5px solid #63b3ed',
                                    borderRadius: '5px',
                                    background: '#23272f',
                                    padding: isMobile() ? '2px 8px' : '4px 16px',
                                }}>
                                    Equip to player:
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile() ? 4 : 8 }}>
                                    {teamPlayers.map(player => {
                                        const canEquip = canPlayerEquipSlot(player.character_sheet_id, gear.slot);
                                        const equipmentCounts = getPlayerEquipmentCounts(player.character_sheet_id);
                                        const currentSlotCount = equipmentCounts[gear.slot] || 0;
                                        const slotLimit = EQUIPMENT_LIMITS[gear.slot] || 1;

                                        return (
                                            <button
                                                key={player.id}
                                                onClick={() => handleEquipToPlayer(player.character_sheet_id)}
                                                disabled={isEquipping || !canEquip}
                                                style={{
                                                    padding: isMobile() ? '6px 8px' : '8px 14px',
                                                    backgroundColor: !canEquip ? '#4a5568' : '#4a90e2',
                                                    color: 'white',
                                                    border: !canEquip ? '2px solid #fc8181' : '2px solid #4a90e2',
                                                    borderRadius: '6px',
                                                    cursor: (isEquipping || !canEquip) ? 'not-allowed' : 'pointer',
                                                    opacity: (isEquipping || !canEquip) ? 0.6 : 1,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    fontSize: isMobile() ? 13 : 15,
                                                    ...bebasFont,
                                                    boxShadow: '0 1px 2px #0006',
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <span style={{
                                                    color: !canEquip ? '#fc8181' : '#fff',
                                                    fontWeight: 600
                                                }}>
                                                    {player.name}
                                                </span>
                                                <span style={{
                                                    fontSize: isMobile() ? 11 : 13,
                                                    color: !canEquip ? '#fc8181' : '#90cdf4',
                                                    border: `1.5px solid ${!canEquip ? '#fc8181' : '#90cdf4'}`,
                                                    borderRadius: '4px',
                                                    background: '#181c22',
                                                    padding: '1px 6px',
                                                    marginLeft: 8,
                                                }}>
                                                    {currentSlotCount}/{slotLimit} {gear.slot}
                                                </span>
                                            </button>
                                        );
                                    })}
                                    <button
                                        onClick={() => setShowEquipModal(false)}
                                        style={{
                                            padding: isMobile() ? '6px 12px' : '10px 20px',
                                            backgroundColor: '#4a5568',
                                            color: 'white',
                                            border: '2px solid #4a5568',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: isMobile() ? 13 : 15,
                                            ...bebasFont,
                                            boxShadow: '0 1px 2px #0006',
                                            marginTop: 4,
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

export default GearPanel